import { Chip, ErrorBanner, Skeleton } from '@patchbay/ui'
import type { UseQueryResult } from '@tanstack/react-query'
import { ExternalLink, Phone } from 'lucide-react'
import { describeError } from '../../lib/api/client'
import { safeHref } from '../../lib/safeUrl'
import { type CaseLink, type CaseLinksOut, GROUP_LABEL, groupLinks, REASON_LABEL } from './api'

/** One link: http(s) and launcher schemes open in a new tab; tel: dials in place. */
export function LinkRow({ link }: { link: CaseLink }) {
  const href = safeHref(link.url)
  if (!href) return null
  const tel = href.startsWith('tel:')
  return (
    <li className="min-w-0" data-link-kind={link.kind}>
      <a
        href={href}
        {...(tel ? {} : { target: '_blank', rel: 'noopener noreferrer' })}
        className="flex min-w-0 items-center gap-1.5 rounded-md px-1.5 py-1 text-sm text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
        title={link.note ?? link.url}
      >
        {tel ? (
          <Phone className="size-3.5 shrink-0 text-muted-foreground" />
        ) : (
          <ExternalLink className="size-3.5 shrink-0 text-muted-foreground" />
        )}
        <span className="truncate">{link.label}</span>
      </a>
      {link.note ? <p className="ml-6.5 text-2xs text-subtle">{link.note}</p> : null}
    </li>
  )
}

/** The links and the missing pieces. Missing entries say what is missing and how to fix it, never a guessed URL. */
export function LinksBody({ data }: { data: CaseLinksOut }) {
  const groups = groupLinks(data.links)
  return (
    <div className="space-y-2.5" data-slot="links">
      {groups.length === 0 && data.missing.length === 0 ? (
        <p className="text-sm text-muted-foreground">No links: the case holds no records that link anywhere yet.</p>
      ) : null}
      {groups.map(({ group, links }) => (
        <section key={group} aria-label={GROUP_LABEL[group]}>
          <h4 className="mb-0.5 text-2xs font-medium uppercase tracking-wide text-subtle">{GROUP_LABEL[group]}</h4>
          <ul className="space-y-0.5">
            {links.map((link) => (
              <LinkRow key={`${link.kind}:${link.url}`} link={link} />
            ))}
          </ul>
        </section>
      ))}
      {data.missing.length ? (
        <section aria-label="Missing links" data-slot="missing">
          <h4 className="mb-1 text-2xs font-medium uppercase tracking-wide text-subtle">Missing</h4>
          <ul className="space-y-1.5">
            {data.missing.map((m) => (
              <li key={m.kind} className="text-xs" data-missing-kind={m.kind}>
                <p className="flex flex-wrap items-center gap-1.5">
                  <span className="font-medium text-foreground/85">{m.label}</span>
                  <Chip size="sm" tone={m.reason === 'unreachable' ? 'warning' : 'neutral'}>
                    {REASON_LABEL[m.reason]}
                  </Chip>
                </p>
                <p className="text-muted-foreground">{m.detail}</p>
                {m.fix ? <p className="text-subtle">{m.fix}</p> : null}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  )
}

export function LinksState({ q }: { q: UseQueryResult<CaseLinksOut> }) {
  if (q.isPending) return <Skeleton className="h-16 w-full" />
  if (q.isError) {
    return <ErrorBanner tone="warning" title={`Links unavailable: ${describeError(q.error)}`} onRetry={() => q.refetch()} />
  }
  return <LinksBody data={q.data} />
}
